import {
  IconDefinition,
  faBug,
  faCircleExclamation,
  faClock,
  faEnvelopeCircleCheck,
  faKey,
  faFileCode,
  faFolder,
  faMedal,
  faQuestionCircle,
  faReceipt,
  faRefresh,
  faServer,
  faTags,
  faUserCheck,
  faUserGroup,
  faUserLock,
  faUserMinus,
  faUserPen,
  faUserPlus,
  faUserSlash,
} from '@fortawesome/free-solid-svg-icons';
import { ReactElement } from 'react';
import { Tag } from '.';
import { EventType } from '../types';
import classNames from '../utilities/class-names';
import eventTypeColours from '../utilities/event-type-colours';

type EventTypeTagStyle = {
  icon: IconDefinition;
  className: string;
  label: string;
  colour: string;
};

const eventTypeTagStyles: {
  [key in EventType]: EventTypeTagStyle;
} = {
  system: {
    icon: faServer,
    className: 'event-type-tag-system',
    label: 'System',
    colour: eventTypeColours['system'],
  },
  unknown: {
    icon: faQuestionCircle,
    className: 'event-type-tag-unknown',
    label: 'Unknown',
    colour: eventTypeColours['unknown'],
  },
  test: {
    icon: faBug,
    className: 'event-type-tag-test',
    label: 'Test',
    colour: eventTypeColours['test'],
  },
  'user-created': {
    icon: faUserPlus,
    className: 'event-type-tag-user-created',
    label: 'User created',
    colour: eventTypeColours['user-created'],
  },
  'user-updated': {
    icon: faUserPen,
    className: 'event-type-tag-user-updated',
    label: 'User updated',
    colour: eventTypeColours['user-updated'],
  },
  'user-deleted': {
    icon: faUserMinus,
    className: 'event-type-tag-user-deleted',
    label: 'User deleted',
    colour: eventTypeColours['user-deleted'],
  },
  'user-deleted-self': {
    icon: faUserMinus,
    className: 'event-type-tag-user-deleted-self',
    label: 'User deleted self',
    colour: eventTypeColours['user-deleted-self'],
  },
  'user-registered': {
    icon: faUserCheck,
    className: 'event-type-tag-user-registered',
    label: 'User registered',
    colour: eventTypeColours['user-registered'],
  },
  'user-forgot-password': {
    icon: faUserSlash,
    className: 'event-type-tag-user-forgot-password',
    label: 'User forgot password',
    colour: eventTypeColours['user-forgot-password'],
  },
  'user-change-password': {
    icon: faUserPen,
    className: 'event-type-tag-user-change-password',
    label: 'User change password',
    colour: eventTypeColours['user-change-password'],
  },
  'token-created': {
    icon: faKey,
    className: 'event-type-tag-token-created',
    label: 'Token created',
    colour: eventTypeColours['token-created'],
  },
  'token-updated': {
    icon: faKey,
    className: 'event-type-tag-token-updated',
    label: 'Token updated',
    colour: eventTypeColours['token-updated'],
  },
  'token-regenerated': {
    icon: faKey,
    className: 'event-type-tag-token-regenerated',
    label: 'Token regenerated',
    colour: eventTypeColours['token-regenerated'],
  },
  'token-deleted': {
    icon: faKey,
    className: 'event-type-tag-token-deleted',
    label: 'Token deleted',
    colour: eventTypeColours['token-deleted'],
  },
  'list-created': {
    icon: faFolder,
    className: 'event-type-tag-list-created',
    label: 'List created',
    colour: eventTypeColours['list-created'],
  },
  'list-updated': {
    icon: faFolder,
    className: 'event-type-tag-list-updated',
    label: 'List updated',
    colour: eventTypeColours['list-updated'],
  },
  'list-deleted': {
    icon: faFolder,
    className: 'event-type-tag-list-deleted',
    label: 'List deleted',
    colour: eventTypeColours['list-deleted'],
  },
  'index-created': {
    icon: faTags,
    className: 'event-type-tag-index-created',
    label: 'Index created',
    colour: eventTypeColours['index-created'],
  },
  'index-updated': {
    icon: faTags,
    className: 'event-type-tag-index-updated',
    label: 'Index updated',
    colour: eventTypeColours['index-updated'],
  },
  'index-deleted': {
    icon: faTags,
    className: 'event-type-tag-index-deleted',
    label: 'Index deleted',
    colour: eventTypeColours['index-deleted'],
  },
  'index-built': {
    icon: faTags,
    className: 'event-type-tag-index-built',
    label: 'Index built',
    colour: eventTypeColours['index-built'],
  },
  'index-build-failed': {
    icon: faCircleExclamation,
    className: 'event-type-tag-index-build-failed',
    label: 'Index build failed',
    colour: eventTypeColours['index-build-failed'],
  },
  'index-build-requested': {
    icon: faRefresh,
    className: 'event-type-tag-index-build-requested',
    label: 'Index rebuild requested',
    colour: eventTypeColours['index-build-requested'],
  },
  'item-created': {
    icon: faFileCode,
    className: 'event-type-tag-item-created',
    label: 'Item created',
    colour: eventTypeColours['item-created'],
  },
  'item-updated': {
    icon: faFileCode,
    className: 'event-type-tag-item-updated',
    label: 'Item updated',
    colour: eventTypeColours['item-updated'],
  },
  'item-deleted': {
    icon: faFileCode,
    className: 'event-type-tag-item-deleted',
    label: 'Item deleted',
    colour: eventTypeColours['item-deleted'],
  },
  'item-restored': {
    icon: faFileCode,
    className: 'event-type-tag-item-restored',
    label: 'Item restored',
    colour: eventTypeColours['item-restored'],
  },
  'identity-created': {
    icon: faUserPlus,
    className: 'event-type-tag-identity-created',
    label: 'Identity created',
    colour: eventTypeColours['identity-created'],
  },
  'identity-updated': {
    icon: faUserPen,
    className: 'event-type-tag-identity-updated',
    label: 'Identity updated',
    colour: eventTypeColours['identity-updated'],
  },
  'identity-deleted': {
    icon: faUserMinus,
    className: 'event-type-tag-identity-deleted',
    label: 'Identity deleted',
    colour: eventTypeColours['identity-deleted'],
  },
  'identity-registered': {
    icon: faUserCheck,
    className: 'event-type-tag-identity-registered',
    label: 'Identity registered',
    colour: eventTypeColours['identity-registered'],
  },
  'identity-logged-in': {
    icon: faUserCheck,
    className: 'event-type-tag-identity-logged-in',
    label: 'Identity logged in',
    colour: eventTypeColours['identity-logged-in'],
  },
  'identity-logged-out': {
    icon: faUserSlash,
    className: 'event-type-tag-identity-logged-out',
    label: 'Identity logged out',
    colour: eventTypeColours['identity-logged-out'],
  },
  'identity-updated-self': {
    icon: faUserPen,
    className: 'event-type-tag-identity-updated-self',
    label: 'Identity updated self',
    colour: eventTypeColours['identity-updated-self'],
  },
  'identity-deleted-self': {
    icon: faUserMinus,
    className: 'event-type-tag-identity-deleted-self',
    label: 'Identity deleted self',
    colour: eventTypeColours['identity-deleted-self'],
  },
  'identity-sessions-revoked': {
    icon: faUserSlash,
    className: 'event-type-tag-identity-sessions-revoked',
    label: 'Identity sessions revoked',
    colour: eventTypeColours['identity-sessions-revoked'],
  },
  'identity-password-reset-requested': {
    icon: faKey,
    className: 'event-type-tag-identity-password-reset-requested',
    label: 'Identity password reset requested',
    colour: eventTypeColours['identity-password-reset-requested'],
  },
  'identity-password-reset': {
    icon: faUserLock,
    className: 'event-type-tag-identity-password-reset',
    label: 'Identity password reset',
    colour: eventTypeColours['identity-password-reset'],
  },
  'identity-email-verification-requested': {
    icon: faEnvelopeCircleCheck,
    className: 'event-type-tag-identity-email-verification-requested',
    label: 'Identity email verification requested',
    colour: eventTypeColours['identity-email-verification-requested'],
  },
  'identity-email-verified': {
    icon: faEnvelopeCircleCheck,
    className: 'event-type-tag-identity-email-verified',
    label: 'Identity email verified',
    colour: eventTypeColours['identity-email-verified'],
  },
  'identity-provider-linked': {
    icon: faUserCheck,
    className: 'event-type-tag-identity-provider-linked',
    label: 'Identity provider linked',
    colour: eventTypeColours['identity-provider-linked'],
  },
  'identity-provider-unlinked': {
    icon: faUserSlash,
    className: 'event-type-tag-identity-provider-unlinked',
    label: 'Identity provider unlinked',
    colour: eventTypeColours['identity-provider-unlinked'],
  },
  'identity-group-created': {
    icon: faUserGroup,
    className: 'event-type-tag-identity-group-created',
    label: 'Identity group created',
    colour: eventTypeColours['identity-group-created'],
  },
  'identity-group-updated': {
    icon: faUserGroup,
    className: 'event-type-tag-identity-group-updated',
    label: 'Identity group updated',
    colour: eventTypeColours['identity-group-updated'],
  },
  'identity-group-deleted': {
    icon: faUserGroup,
    className: 'event-type-tag-identity-group-deleted',
    label: 'Identity group deleted',
    colour: eventTypeColours['identity-group-deleted'],
  },
  'identity-group-provider-updated': {
    icon: faUserGroup,
    className: 'event-type-tag-identity-group-provider-updated',
    label: 'Identity group provider updated',
    colour: eventTypeColours['identity-group-provider-updated'],
  },
  'identity-group-provider-deleted': {
    icon: faUserGroup,
    className: 'event-type-tag-identity-group-provider-deleted',
    label: 'Identity group provider deleted',
    colour: eventTypeColours['identity-group-provider-deleted'],
  },
  'identity-group-webhook-delivered': {
    icon: faUserCheck,
    className: 'event-type-tag-identity-group-webhook-delivered',
    label: 'Identity group webhook delivered',
    colour: eventTypeColours['identity-group-webhook-delivered'],
  },
  'identity-group-webhook-failed': {
    icon: faCircleExclamation,
    className: 'event-type-tag-identity-group-webhook-failed',
    label: 'Identity group webhook failed',
    colour: eventTypeColours['identity-group-webhook-failed'],
  },
  'setting-updated': {
    icon: faClock,
    className: 'event-type-tag-setting-updated',
    label: 'Setting updated',
    colour: eventTypeColours['setting-updated'],
  },
  'subscription-created': {
    icon: faMedal,
    className: 'event-type-tag-subscription-created',
    label: 'Subscription created',
    colour: eventTypeColours['subscription-created'],
  },
  'subscription-updated': {
    icon: faMedal,
    className: 'event-type-tag-subscription-updated',
    label: 'Subscription updated',
    colour: eventTypeColours['subscription-updated'],
  },
  'subscription-deleted': {
    icon: faMedal,
    className: 'event-type-tag-subscription-deleted',
    label: 'Subscription deleted',
    colour: eventTypeColours['subscription-deleted'],
  },
  'subscription-subscribed': {
    icon: faMedal,
    className: 'event-type-tag-subscription-subscribed',
    label: 'Subscription subscribed',
    colour: eventTypeColours['subscription-subscribed'],
  },
  'subscription-cancelled': {
    icon: faMedal,
    className: 'event-type-tag-subscription-cancelled',
    label: 'Subscription cancelled',
    colour: eventTypeColours['subscription-cancelled'],
  },
  'subscription-payment-succeeded': {
    icon: faReceipt,
    className: 'event-type-tag-subscription-payment-succeeded',
    label: 'Subscription payment succeeded',
    colour: eventTypeColours['subscription-payment-succeeded'],
  },
  'subscription-payment-failed': {
    icon: faCircleExclamation,
    className: 'event-type-tag-subscription-payment-failed',
    label: 'Subscription payment failed',
    colour: eventTypeColours['subscription-payment-failed'],
  },
};

type EventTypeTagProps = {
  eventType: EventType;
};

export function EventTypeTag({ eventType }: EventTypeTagProps): ReactElement {
  let { icon, className, label, colour } = eventTypeTagStyles[eventType];

  return (
    <Tag
      show
      className={classNames('event-type-tag', className)}
      icon={icon}
      colour={colour}
    >
      {label}
    </Tag>
  );
}
